import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from '@emotion/styled';
import { Button, Chip, Stack } from '@mui/material';
import {
  DataGrid as MuiDataGrid,
  GridToolbarContainer,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Tune as TuneIcon,
  FlightTakeoff as FlightTakeoffIcon,
  ToggleOn as ToggleOnIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';

import { breadcrumbsActions } from '@store/breadcrumbs';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import OrchestrationRulesFilter from './OrchestrationRulesFilter';

import {
  paymentOrchestrationRulesActions,
  getPaymentOrchestrationRuleList,
  selectOrchestrationRuleFilteredList,
  selectIsLoadingPaymentOrchestrationRuleList,
  selectFilteredSource,
  selectFilteredStatus,
  selectFilteredEffectiveStartDate,
  selectFilteredEffectiveEndDate,
} from '../../store/payment-orchestration-rules.slice';

import {
  RULE_ADD_ROUTE,
  RULE_LIST_ROUTE,
  ruleListBreadcrumb,
} from '../../payment-orchestration.breadcrumbs';
import {
  getRuleListValue,
  getRuleListFormattedValue,
  getRuleListAlignment,
} from '../../payment-orchestration.utils';
import {
  PAGE_DETAILS,
  COLUMN_WIDTH,
  PAYMENT_ORCHESTRATION_RULE_STATUS,
  PAYMENT_ORCHESTRATION_RULE_WEIGHTAGE_TYPE,
  PAYMENT_ORCHESTRATION_RULE_COLUMNS,
  PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT,
} from '../../payment-orchestration.constants';

function ActionToolbar({
  navigate,
  onFilterClick,
  filteredSource,
  onSourceRemoval,
  filteredStatus,
  onStatusRemoval,
  filteredEffectiveStartDate,
  filteredEffectiveEndDate,
  onEffectiveDateRangeRemoval,
  permissions,
}) {
  return (
    <GridToolbarContainer
      sx={{
        padding: '10px',
        gap: '10px',
        justifyContent: 'space-between',
      }}>
      <Stack
        flex={1}
        direction="row"
        gap="8px"
        flexWrap="wrap">
        {filteredSource.map((source) => (
          <Chip
            icon={<FlightTakeoffIcon />}
            key={source}
            label={source}
            onDelete={() => {
              onSourceRemoval(source);
            }}
          />
        ))}
        {filteredStatus.map((status) => (
          <Chip
            icon={<ToggleOnIcon />}
            key={status}
            label={status}
            onDelete={() => {
              onStatusRemoval(status);
            }}
          />
        ))}
        {(filteredEffectiveStartDate || filteredEffectiveEndDate) && (
          <Chip
            icon={<DateRangeIcon />}
            label={`${
              filteredEffectiveStartDate
                ? dayjs(filteredEffectiveStartDate).format(
                    PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT,
                  )
                : 'No Start Date'
            } - ${
              filteredEffectiveEndDate
                ? dayjs(filteredEffectiveEndDate).format(
                    PAYMENT_ORCHESTRATION_RULE_DATE_FORMAT,
                  )
                : 'No End Date'
            }`}
            onDelete={onEffectiveDateRangeRemoval}
          />
        )}
      </Stack>
      <Stack
        direction="row"
        gap="10px">
        <Button
          variant="outlined"
          onClick={onFilterClick}
          startIcon={<TuneIcon />}>
          Filter
        </Button>

        <Button
          variant="contained"
          disabled={!permissions?.canCreatePaymentOrchRule}
          onClick={() => navigate(RULE_ADD_ROUTE)}
          startIcon={<AddIcon />}>
          Add new rule
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
}

const DataGrid = styled(MuiDataGrid)`
  & .MuiDataGrid-columnHeader [class~='MuiDataGrid-columnHeaderTitle'] {
    white-space-collapse: break-spaces;
    text-wrap: wrap;
    line-height: normal;
  }

  &
    .MuiDataGrid-columnHeader--alignRight
    [class~='MuiDataGrid-columnHeaderTitle'] {
    text-align: right;
  }

  &
    .MuiDataGrid-columnHeader--alignCenter
    [class~='MuiDataGrid-columnHeaderTitle'] {
    text-align: center;
  }
`;

export function getStatusCustomColumn(status) {
  let color;
  switch (status) {
    case PAYMENT_ORCHESTRATION_RULE_STATUS.DISABLED:
      color = 'error';
      break;
    case PAYMENT_ORCHESTRATION_RULE_STATUS.ENABLED:
      color = 'success';
      break;
    case PAYMENT_ORCHESTRATION_RULE_STATUS.FORCE_DISABLED:
      color = 'warning';
      break;

    default:
      color = 'info';
      break;
  }

  return (
    <Chip
      label={status}
      color={color}
      variant="outlined"
    />
  );
}

function getCurrencyWeightageCustomColumn({ weightageType }, value) {
  return (
    <Stack
      gap="8px"
      p="8px">
      {Object.keys(value).map((currency) => (
        <Chip
          key={currency}
          label={`${currency}: ${value[currency]}${
            weightageType ===
            PAYMENT_ORCHESTRATION_RULE_WEIGHTAGE_TYPE.PERCENTAGE
              ? '%'
              : ''
          }`}
          variant="outlined"
        />
      ))}
    </Stack>
  );
}

function getRuleListCustomColumn(row, value, field) {
  switch (field) {
    case 'status':
      return getStatusCustomColumn(value);

    case 'currencyWeightage':
      return getCurrencyWeightageCustomColumn(row, value);

    default:
      return null;
  }
}

function getRuleListColumns() {
  return PAYMENT_ORCHESTRATION_RULE_COLUMNS.map(
    ({ width = COLUMN_WIDTH.MD, ...col }) => ({
      ...col,
      width,
      sortable: false,
      headerAlign: getRuleListAlignment(col.field),
      align: getRuleListAlignment(col.field),
      valueGetter: ({ row }) => getRuleListValue(row, col.field),
      valueFormatter: ({ value }) =>
        getRuleListFormattedValue(value, col.field),
      renderCell: ({ value, row }) =>
        getRuleListCustomColumn(row, value, col.field),
    }),
  );
}

function PaymentOrchestrationRules({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  const ruleList = useSelector(selectOrchestrationRuleFilteredList);
  const isLoadingRuleList = useSelector(
    selectIsLoadingPaymentOrchestrationRuleList,
  );
  const filteredSource = useSelector(selectFilteredSource);
  const filteredStatus = useSelector(selectFilteredStatus);
  const filteredEffectiveStartDate = useSelector(
    selectFilteredEffectiveStartDate,
  );
  const filteredEffectiveEndDate = useSelector(selectFilteredEffectiveEndDate);

  useEffect(() => {
    if (!permissions?.canViewPaymentOrchRule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    dispatch(breadcrumbsActions.setBreadcrumbs(ruleListBreadcrumb));
  }, [dispatch]);

  // API: Get Rule List
  useEffect(() => {
    dispatch(getPaymentOrchestrationRuleList());
  }, [dispatch]);

  const onFilterClick = () => {
    setIsFilterModalVisible(true);
  };

  const onSourceRemoval = (source) => {
    dispatch(paymentOrchestrationRulesActions.removeFilterSource(source));
  };
  const onStatusRemoval = (status) => {
    dispatch(paymentOrchestrationRulesActions.removeFilterStatus(status));
  };
  const onEffectiveDateRangeRemoval = () => {
    dispatch(paymentOrchestrationRulesActions.removeFilterEffectiveDateRange());
  };

  return (
    <ApplicationPageTemplate
      title={PAGE_DETAILS.rules.title}
      description={PAGE_DETAILS.rules.description}
      pageReady>
      <DataGrid
        getRowHeight={() => 'auto'}
        loading={isLoadingRuleList}
        density="compact"
        rows={ruleList}
        rowsPerPageOptions={[10]}
        pageSize={10}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        components={{
          Toolbar: () =>
            ActionToolbar({
              navigate,
              onFilterClick,
              filteredSource,
              onSourceRemoval,
              filteredStatus,
              onStatusRemoval,
              filteredEffectiveStartDate,
              filteredEffectiveEndDate,
              onEffectiveDateRangeRemoval,
              permissions,
            }),
        }}
        onRowClick={({ row }) => {
          navigate(`${RULE_LIST_ROUTE}/${row.id}`);
        }}
        getRowId={({ id }) => id}
        columns={getRuleListColumns()}
      />
      <OrchestrationRulesFilter
        isVisible={isFilterModalVisible}
        onClose={setIsFilterModalVisible}
      />
    </ApplicationPageTemplate>
  );
}

export default PaymentOrchestrationRules;
